import { useI18n } from '@ecom/i18n';
import {
  Badge,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@ecom/ui-kit';
import { type ReactElement } from 'react';

import { OUTCOME_LABEL_KEY, OUTCOME_TONE, problemMessage } from '../state';
import type { JibexCheckLine } from '../types';

import { formatDateTime } from './format';

/**
 * « Historique des vérifications » — every save and every test, newest first. Read-only by nature (the history is
 * append-only on the server): there is no edit, no delete, no export. A line carries facts and a CODE that is shown
 * translated — never a free-text carrier message, and never anything secret.
 */
export function JibexCheckHistory({
  checks,
}: {
  readonly checks: readonly JibexCheckLine[];
}): ReactElement {
  const { t, locale } = useI18n();

  return (
    <Card className="flex flex-col gap-3 p-5">
      <h2 className="font-display text-lg font-semibold text-ink">
        {t('merchant.delivery.account.history.title')}
      </h2>

      {checks.length === 0 ? (
        <p className="text-sm text-inkFaint">{t('merchant.delivery.account.history.empty')}</p>
      ) : (
        <Table caption={t('merchant.delivery.account.history.caption')}>
          <TableHead>
            <TableRow>
              <TableHeaderCell>{t('merchant.delivery.account.history.colDate')}</TableHeaderCell>
              <TableHeaderCell>{t('merchant.delivery.account.history.colResult')}</TableHeaderCell>
              <TableHeaderCell>{t('merchant.delivery.account.history.colDetail')}</TableHeaderCell>
              <TableHeaderCell>{t('merchant.delivery.account.history.colClient')}</TableHeaderCell>
              <TableHeaderCell>
                {t('merchant.delivery.account.history.colDuration')}
              </TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {checks.map((check) => {
              const problem = problemMessage(check.errorCode);
              return (
                <TableRow key={check.id}>
                  <TableCell>{formatDateTime(check.checkedAt, locale)}</TableCell>
                  <TableCell>
                    <Badge tone={OUTCOME_TONE[check.outcome]}>
                      {t(OUTCOME_LABEL_KEY[check.outcome])}
                    </Badge>
                  </TableCell>
                  <TableCell>{problem === null ? '—' : t(problem.key, problem.params)}</TableCell>
                  <TableCell>
                    <span className="font-mono text-xs">{check.clientId}</span>
                  </TableCell>
                  <TableCell>
                    {check.durationMs === null
                      ? '—'
                      : t('merchant.delivery.account.history.durationMs', { ms: check.durationMs })}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </Card>
  );
}
